import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Popover, Space, Table, Tabs, Tag, Tooltip, Typography, type TableProps } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import type { ContactListItem, FieldDefinitionDto, RuleGroup } from '@crm/shared';
import { emptyRuleGroup } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { useListParams } from '../../components/list-params';
import { FilterBuilder, FilterSummary } from '../../components/FilterBuilder';
import { ColumnChooser, type ColumnOption, SortableColumns, draggableHeader, orderedFields, sortableHeaderComponents, useVisibleColumns } from '../../components/ColumnChooser';
import { formatValue } from '../../components/DynamicField';
import { downloadCsv } from '../../components/export-csv';
import { ExportButton } from '../../components/ExportButton';
import { readValue } from '../../features/fields/system-keys';
import { useFields } from '../../features/fields/api';
import { contactName, useContacts } from '../../features/contacts/api';
import { useAudienceData, useSegments } from '../../features/audience/api';
import { useScopedFilter } from '../../features/projects/scope';
import { BulkActionsBar } from '../../components/BulkActionsBar';
import { fileUrl } from '../../api/client';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { plural } from '../../utils/format';
import { ContactDrawer } from './ContactDrawer';
import { NameCell, TableSearch } from '../../components/NameCell';
import { LinksView } from '../../components/LinksField';
import { EntityTag, TagGroup } from '../../components/EntityTag';

export const EMAIL_STATUS_COLOR: Record<string, string> = { VALID: 'success', INVALID: 'error', CATCH_ALL: 'warning', DISPOSABLE: 'error', UNKNOWN: 'default', UNVERIFIED: 'default', SUPPRESSED: 'warning' };
export const EMAIL_STATUS_LABEL: Record<string, string> = { VALID: 'Valid', INVALID: 'Invalid', CATCH_ALL: 'Catch-all', DISPOSABLE: 'Disposable', UNKNOWN: 'Unknown', UNVERIFIED: 'Unverified', SUPPRESSED: 'Suppressed' };

/** Longer organization names are cut with "..." in the table. */
export const TABLE_NAME_CHARS = 25;

/** Columns that are not fields but can be shown, hidden and moved like them. */
const EXTRA_COLUMNS: ColumnOption[] = [
  { key: 'tags', label: 'Tags', showInTable: true },
  { key: 'segments', label: 'Segments' },
  { key: 'lists', label: 'Lists' },
];

function TagList({ items }: { items?: { id: string; name: string; color?: string | null }[] }) {
  return (
    <TagGroup>
      {(items ?? []).map((t) => (
        <Tag key={t.id} color={t.color ?? undefined} style={{ marginInlineEnd: 0 }}>
          {t.name}
        </Tag>
      ))}
    </TagGroup>
  );
}

function EntityTags({ kind, items }: { kind: 'segment' | 'list'; items?: { id: string; name: string }[] }) {
  return (
    <TagGroup>
      {(items ?? []).map((t) => (
        <EntityTag key={t.id} kind={kind} id={t.id} to={kind === 'segment' ? `/contacts?segment=${t.id}` : undefined}>
          {t.name}
        </EntityTag>
      ))}
    </TagGroup>
  );
}

export function EmailStatusTag({ status }: { status: string }) {
  return <Tag color={EMAIL_STATUS_COLOR[status] ?? 'default'}>{EMAIL_STATUS_LABEL[status] ?? status}</Tag>;
}

export function ContactsPage() {
  const navigate = useNavigate();
  const { params, setParams, filterGroup, setFilterGroup } = useListParams({ sortBy: 'created_at', sortDir: 'desc' });
  const defs = useFields('CONTACT');
  const orgDefs = useFields('ORGANIZATION');
  const [sp, setSp] = useSearchParams();
  const segmentId = sp.get('segment') ?? undefined;
  // the segment tab narrows the view like an extra filter condition, so search, filters, export and "select all matching" all follow it
  // so does the header's project selection: only contacts tagged, listed or in a segment of that project
  const viewFilter = useScopedFilter(
    useMemo<RuleGroup | undefined>(
      () =>
        segmentId
          ? { combinator: 'and', conditions: [{ kind: 'segment', op: 'in_any', segmentIds: [segmentId] }, ...(filterGroup ? [filterGroup] : [])] }
          : filterGroup,
      [segmentId, filterGroup],
    ),
  );
  const viewParams = useMemo(() => ({ ...params, filter: viewFilter ? JSON.stringify(viewFilter) : undefined }), [params, viewFilter]);
  const contacts = useContacts(viewParams);
  const audience = useAudienceData();
  const segments = useSegments();
  const columnDefs = useMemo<ColumnOption[] | undefined>(() => defs.data && [...defs.data, ...EXTRA_COLUMNS], [defs.data]);
  const { visible, setVisible } = useVisibleColumns('contacts.columns.v2', columnDefs, { from: 'contacts.columns', add: ['tags'] });
  const [selected, setSelected] = useState<string[]>([]);
  const [allMatching, setAllMatching] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState<RuleGroup>(filterGroup ?? emptyRuleGroup());
  const [filterOpen, setFilterOpen] = useState(false);

  const columns = useMemo<TableProps<ContactListItem>['columns']>(() => {
    const d = defs.data ?? [];
    const nameCol = {
      title: 'Name',
      key: 'name',
      fixed: 'left' as const,
      sorter: true,
      sortOrder: params.sortBy === 'first_name' ? (params.sortDir === 'asc' ? ('ascend' as const) : ('descend' as const)) : null,
      render: (_: unknown, c: ContactListItem) => <NameCell name={contactName(c)} sub={c.email ?? c.organization?.name} to={`/contacts/${c.id}`} image={fileUrl(c.photoFileId)} copyable copySub={c.email ?? undefined} />,
    };
    const extraCols: Record<string, (c: ContactListItem) => React.ReactNode> = {
      tags: (c) => <TagList items={c.tags} />,
      segments: (c) => <EntityTags kind="segment" items={c.segments} />,
      lists: (c) => <EntityTags kind="list" items={c.lists} />,
    };
    const fieldCols = orderedFields([...d, ...EXTRA_COLUMNS], visible)
      .filter((f) => !['first_name', 'last_name', 'email'].includes(f.key))
      .map((f) => {
        const extra = extraCols[f.key];
        if (extra) return { title: f.label, key: f.key, onHeaderCell: () => draggableHeader(f.key), render: (_: unknown, c: ContactListItem) => extra(c) };
        const def = f as FieldDefinitionDto;
        return {
          title: def.label,
          key: def.key,
          onHeaderCell: () => draggableHeader(def.key),
          ellipsis: true,
          sorter: true,
          sortOrder: params.sortBy === def.key ? (params.sortDir === 'asc' ? ('ascend' as const) : ('descend' as const)) : null,
          render: (_: unknown, c: ContactListItem) => renderCell(def, c, TABLE_NAME_CHARS),
        };
      });
    return [nameCol, ...fieldCols];
  }, [defs.data, visible, params.sortBy, params.sortDir]);

  const onChange: TableProps<ContactListItem>['onChange'] = (pagination, _f, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = s?.columnKey === 'name' ? 'first_name' : (s?.columnKey as string | undefined);
    setParams({
      page: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 25,
      sortBy: s?.order ? key : undefined,
      sortDir: s?.order ? (s.order === 'ascend' ? 'asc' : 'desc') : undefined,
    });
  };

  const total = contacts.data?.total ?? 0;
  const pageIds = (contacts.data?.items ?? []).map((c) => c.id);
  const pageFullySelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const selection = allMatching ? { all: { q: params.q, filter: viewFilter } } : { ids: selected };
  const selectionCount = allMatching ? total : selected.length;
  const clearSelection = () => {
    setSelected([]);
    setAllMatching(false);
  };

  const activeFilters = filterGroup?.conditions.length ?? 0;

  const selectSegment = (key: string) => {
    const next = new URLSearchParams(sp);
    if (key === 'all') next.delete('segment');
    else next.set('segment', key);
    next.delete('page');
    setSp(next, { replace: true });
    clearSelection();
  };

  return (
    <div className="page">
      <PageHeader
        title="Contacts"
        subtitle={contacts.data ? plural(contacts.data.total, 'contact') : undefined}
        extra={
          <Space>
            <Button onClick={() => navigate('/tools/imports/new')}>Import</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer(true)}>
              New contact
            </Button>
          </Space>
        }
      />
      <Card>
      {!!segments.data?.length && (
        <Tabs
          activeKey={segmentId ?? 'all'}
          onChange={selectSegment}
          style={{ marginTop: -12 }}
          items={[
            { key: 'all', label: 'All contacts' },
            ...[...segments.data].sort((a, b) => b.memberCount - a.memberCount || a.name.localeCompare(b.name)).map((s) => ({
              key: s.id,
              label: (
                <Space size={6}>
                  {s.name}
                  <Tag style={{ marginInlineEnd: 0 }}>{s.memberCount.toLocaleString()}</Tag>
                </Space>
              ),
            })),
          ]}
        />
      )}
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={params.q} onSearch={(q) => setParams({ q: q || undefined })} placeholder="Search name, email, phone, organization" width={340} />
          <FilterSummary group={filterGroup} fields={[...(defs.data ?? []), ...(orgDefs.data ?? [])]} audience={audience} />
        </div>
        <div className="toolbar-group">
        <Popover
          open={filterOpen}
          onOpenChange={(o) => { setFilterOpen(o); if (o) setDraft(filterGroup ?? emptyRuleGroup()); }}
          trigger="click"
          placement="bottomLeft"
          content={
            <div>
              <FilterBuilder value={draft} onChange={setDraft} contactFields={defs.data ?? []} organizationFields={orgDefs.data ?? []} audience={audience} />
              <Space style={{ marginTop: 12 }}>
                <Button type="primary" size="small" onClick={() => { setFilterGroup(draft); setFilterOpen(false); }}>Apply</Button>
                <Button size="small" onClick={() => { setDraft(emptyRuleGroup()); setFilterGroup(undefined); setFilterOpen(false); }}>Clear</Button>
              </Space>
            </div>
          }
        >
          <Badge count={activeFilters} size="small">
            <Button icon={<FilterOutlined />}>Filter</Button>
          </Badge>
        </Popover>
        <ColumnChooser defs={(columnDefs ?? []).filter((f) => !['first_name', 'last_name', 'email'].includes(f.key))} visible={visible} onChange={setVisible} />
        <ExportButton onExport={() => downloadCsv('/contacts/export.csv', viewParams)} count={contacts.data?.total} noun="contact" />
        </div>
      </div>
      {selectionCount > 0 && (
        <div className="bulk-bar" style={{ background: '#EEF2FE', borderRadius: 10, padding: '8px 12px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <BulkActionsBar selection={selection} count={selectionCount} contactFields={defs.data ?? []} onDone={(action) => action === 'delete' && clearSelection()} />
          <Space>
            {pageFullySelected && !allMatching && total > selected.length && (
              <Button size="small" type="link" onClick={() => setAllMatching(true)}>
                Select all {total.toLocaleString()} matching
              </Button>
            )}
            <Button size="small" type="link" onClick={clearSelection}>
              Clear selection
            </Button>
          </Space>
        </div>
      )}
      <SortableColumns visible={visible} onChange={setVisible}>
      <Table<ContactListItem>
        components={sortableHeaderComponents}
        rowKey="id"
        size="middle"
        loading={tableLoading(contacts)}
        dataSource={contacts.data?.items ?? []}
        columns={columns}
        locale={tableLocale(
          contacts,
          params.q || activeFilters ? (
            <EmptyState description="No contacts match this search or filter." action={<Button onClick={() => { setParams({ q: undefined }); setFilterGroup(undefined); }}>Clear search and filters</Button>} />
          ) : (
            <EmptyState description="No contacts yet. Add one, import a file or share a form." action={<Space><Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer(true)}>New contact</Button><Button onClick={() => navigate('/tools/imports/new')}>Import</Button></Space>} />
          ),
        )}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: allMatching ? pageIds : selected,
          onChange: (keys) => {
            setAllMatching(false);
            setSelected(keys as string[]);
          },
        }}
        onChange={onChange}
        pagination={{ current: params.page, pageSize: params.pageSize, total: contacts.data?.total ?? 0, showSizeChanger: true, showTotal: (t) => `${t.toLocaleString()} total` }}
        onRow={(c) => ({
          style: { cursor: 'pointer' },
          // a click anywhere on the row opens the contact, except on its own controls (checkbox, links, tags) or after selecting text
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('a, button, input, label, .ant-checkbox-wrapper, .ant-table-selection-column, .ant-tag')) return;
            if (window.getSelection()?.toString()) return;
            if (e.metaKey || e.ctrlKey) window.open(`/contacts/${c.id}`, '_blank');
            else navigate(`/contacts/${c.id}`);
          },
        })}
      />
      </SortableColumns>
      </Card>
      <ContactDrawer open={drawer} onClose={() => setDrawer(false)} defs={defs.data ?? []} onSaved={(c) => navigate(`/contacts/${c.id}`)} />
    </div>
  );
}

/** Organization link tag, with the logo when there is one. */
export function OrgLink({ org, maxChars }: { org: NonNullable<ContactListItem['organization']>; maxChars?: number }) {
  return <EntityTag kind="organization" id={org.id} image={fileUrl(org.logoFileId)} maxChars={maxChars}>{org.name}</EntityTag>;
}

/** "+2" after the primary value when a contact has more addresses or numbers; hover lists them all. */
function moreCount(list: unknown[] | undefined, all: string[] | undefined) {
  if (!list || list.length < 2) return null;
  return (
    <Tooltip title={all?.join(', ')}>
      <Tag style={{ marginLeft: 6, marginRight: 0 }}>+{list.length - 1}</Tag>
    </Tooltip>
  );
}

/** `maxChars` shortens organization names (the table passes it; profile pages show them in full). */
export function renderCell(f: FieldDefinitionDto, c: ContactListItem, maxChars?: number) {
  if (f.key === 'organization_id') return <TagGroup>{(c.organizations ?? (c.organization ? [c.organization] : [])).map((o) => <OrgLink key={o.id} org={o} maxChars={maxChars} />)}</TagGroup>;
  if (f.key === 'email_status') return <EmailStatusTag status={c.emailStatus} />;
  if (f.key === 'email' && c.email) return <span><Typography.Text copyable={{ text: c.email }}>{c.email}</Typography.Text>{moreCount(c.emails, c.emails?.map((e) => e.email))}</span>;
  if (f.key === 'job_title' && c.jobTitle) return <Typography.Text copyable={{ text: c.jobTitle }}>{c.jobTitle}</Typography.Text>;
  if (f.key === 'mobile' && c.mobile) return <Typography.Text copyable={{ text: c.mobile }}>{c.mobile}</Typography.Text>;
  if (f.key === 'phone' && c.phone) return <span><Typography.Text copyable={{ text: c.phone }}>{c.phone}</Typography.Text>{moreCount(c.phones, c.phones?.map((p) => (p.label ? `${p.number} (${p.label})` : p.number)))}</span>;
  const v = readValue(f, c as unknown as Record<string, unknown>);
  if (f.type === 'LINKS') return <LinksView value={v} inline />;
  if (f.type === 'URL' && v) return <a href={String(v).startsWith('http') ? String(v) : `https://${String(v)}`} target="_blank" rel="noreferrer">{String(v)}</a>;
  return formatValue(f, v);
}
